import { useEffect, useState } from 'react';
import { Eye, Wallet } from 'lucide-react';
import { getCompanyComplianceSettings, updateCompanyComplianceSettings } from '../../api/officeandcompany';
import { salaryApi } from '../../api/salary';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { useAccess } from '../../contexts/AccessContext';
import { Popup } from '../../components/Popup';

// Access-Management panel key for this page (see config/panelCatalog.ts).
// View = read the defaults + liability report; Manage = edit and save.
const PANEL_KEY = 'set-salary-compliance';

// The two org-wide payroll modes, in display order. `applyInPayroll` is the
// stored flag and reads inverted on purpose: "Without retainer-ship amount"
// (the retainer IS deducted) is `true`, because the label describes the
// take-home while the flag describes the machinery.
const SALARY_CALC_MODES: {
  applyInPayroll: boolean;
  title: string;
  description: string;
  example?: string;
}[] = [
  {
    applyInPayroll: false,
    title: 'With retainer-ship amount',
    description: 'Full net pay — nothing is deducted. Anything already withheld stays on hold.',
  },
  {
    applyInPayroll: true,
    title: 'Without retainer-ship amount',
    description: 'The retainer comes off the salary first, then attendance is calculated on the reduced amount. The pool pays out when the period ends.',
    example: '₹33,333 − ₹10,000 = ₹23,333',
  },
];

export default function SalaryCompliance() {
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();
  const { canManage } = useAccess();
  const readOnly = !canManage(PANEL_KEY);
  const [form, setForm] = useState({
    retainerEnabled: false,
    retainerDefaultAmount: 0,
    retainerDefaultPeriod: 3,
    retainerDefaultFloor: 0,
    retainerDefaultRecurring: false,
    // Org-wide payroll switch. true = salary WITHOUT retainer-ship amount
    // (the retainer is deducted/withheld from net pay); false = salary WITH
    // retainer-ship amount (full net, nothing withheld). The flag name
    // describes the machinery (does payroll apply the retainer?), the labels
    // describe the take-home — hence the inversion.
    retainerApplyInPayroll: true,
  });
  const [report, setReport] = useState<{ total_outstanding: number; items: any[] }>({ total_outstanding: 0, items: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getCompanyComplianceSettings();
      const s = Array.isArray(res) ? res[0] : res?.data ? (Array.isArray(res.data) ? res.data[0] : res.data) : res;
      if (s) {
        setForm((p) => ({
          ...p,
          retainerEnabled: s.retainerEnabled === true || s.retainerEnabled === 1,
          retainerDefaultAmount: Number(s.retainerDefaultAmount) || 0,
          retainerDefaultPeriod: Number(s.retainerDefaultPeriod) || 3,
          retainerDefaultFloor: Number(s.retainerDefaultFloor) || 0,
          retainerDefaultRecurring: s.retainerDefaultRecurring === true || s.retainerDefaultRecurring === 1,
          // Missing/NULL (pre-migration row) means "with retainer-ship".
          retainerApplyInPayroll: !(s.retainerApplyInPayroll === false || s.retainerApplyInPayroll === 0),
        }));
      }
    } catch {
      /* silent empty-state fallback (no load-failure toast) */
    }
    try {
      const r = await salaryApi.getRetainerReport();
      setReport(r.data || { total_outstanding: 0, items: [] });
    } catch {
      setReport({ total_outstanding: 0, items: [] });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (readOnly) {
      showError('Read-only access', 'You have view-only access to Salary & Compliance.');
      return;
    }
    setSaving(true);
    try {
      // POST /employee-tax-config upserts the branch-level row; send the retainer
      // defaults merged onto the existing config (server keeps untouched fields).
      const existing = await getCompanyComplianceSettings();
      const base = Array.isArray(existing) ? existing[0] : existing?.data ?? existing ?? {};
      await updateCompanyComplianceSettings({ ...base, ...form });
      showSuccess('Saved', 'Retainer defaults updated');
    } catch (err: any) {
      showError('Save failed', err?.response?.data?.detail || err?.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Wallet className="h-6 w-6 text-emerald-700" />
          <h1 className="text-xl font-semibold">Salary &amp; Compliance</h1>
          {readOnly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1">
              <Eye className="h-3.5 w-3.5" /> View only
            </span>
          )}
        </div>

        <div className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)] space-y-4">
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" disabled={readOnly}
              checked={form.retainerEnabled}
              onChange={(e) => setForm({ ...form, retainerEnabled: e.target.checked })} />
            Enable retainer-ship org-wide
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Default amount (₹/mo)</label>
              <input type="number" className="border rounded p-1 text-sm w-full mt-1 disabled:bg-gray-50 disabled:text-gray-500" disabled={readOnly}
                value={form.retainerDefaultAmount || ''} placeholder="0"
                onChange={(e) => setForm({ ...form, retainerDefaultAmount: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Default period (months)</label>
              <input type="number" min={1} className="border rounded p-1 text-sm w-full mt-1 disabled:bg-gray-50 disabled:text-gray-500" disabled={readOnly}
                value={form.retainerDefaultPeriod || ''} placeholder="3"
                onChange={(e) => setForm({ ...form, retainerDefaultPeriod: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Take-home floor (₹)</label>
              <input type="number" className="border rounded p-1 text-sm w-full mt-1 disabled:bg-gray-50 disabled:text-gray-500" disabled={readOnly}
                value={form.retainerDefaultFloor || ''} placeholder="0"
                onChange={(e) => setForm({ ...form, retainerDefaultFloor: e.target.value === '' ? 0 : Number(e.target.value) })} />
            </div>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" className="h-4 w-4 text-blue-600 rounded" disabled={readOnly}
                checked={form.retainerDefaultRecurring}
                onChange={(e) => setForm({ ...form, retainerDefaultRecurring: e.target.checked })} />
              Recurring by default
            </label>
          </div>

          {/* Org-wide payroll mode: is the employee's salary paid WITH the
              retainer-ship amount (full net, no deduction) or WITHOUT it (the
              retainer is deducted into the pool and paid out later)? Radio
              pair, not a checkbox, so both outcomes are spelled out. */}
          <fieldset className="border-t border-[#f0ece2] pt-4">
            <legend className="text-xs font-semibold text-gray-500 uppercase mb-2">Salary calculation</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {SALARY_CALC_MODES.map((mode) => {
                const selected = form.retainerApplyInPayroll === mode.applyInPayroll;
                return (
                  <label key={mode.title}
                    className={`flex items-start gap-2.5 rounded-xl border p-3 transition-colors ${
                      readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                    } ${
                      selected
                        ? 'border-emerald-600 bg-emerald-50/60'
                        : 'border-[#e8e3d8] bg-white hover:border-emerald-300 hover:bg-[#fdfcf9]'
                    }`}>
                    {/* The native dot is hidden rather than removed: it keeps
                        label clicks, focus and arrow-key navigation working. */}
                    <input type="radio" name="retainerCalcMode" className="peer sr-only" disabled={readOnly}
                      checked={selected}
                      onChange={() => setForm({ ...form, retainerApplyInPayroll: mode.applyInPayroll })} />
                    <span aria-hidden="true"
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-500/40 ${
                        selected ? 'border-emerald-600' : 'border-gray-300'
                      }`}>
                      <span className={`h-1.5 w-1.5 rounded-full bg-emerald-600 transition-transform ${selected ? 'scale-100' : 'scale-0'}`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800">{mode.title}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-gray-500">{mode.description}</span>
                      {mode.example && (
                        <span className="mt-1.5 inline-block rounded-md border border-[#e8e3d8] bg-white px-2 py-0.5 text-[11px] text-gray-600 tabular-nums">
                          {mode.example}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {form.retainerApplyInPayroll && !form.retainerEnabled && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Retainer-ship is not enabled org-wide (checkbox above), so only employees with
                retainer-ship configured on their own profile will have the amount deducted.
                Tick &ldquo;Enable retainer-ship org-wide&rdquo; and set a default amount to deduct it for everyone.
              </p>
            )}
          </fieldset>
          {readOnly ? (
            <p className="text-sm text-gray-500">
              You have view-only access to this page. Ask an admin for Manage on
              Settings → Salary &amp; Compliance to edit these defaults.
            </p>
          ) : (
            <button onClick={save} disabled={saving}
              className="bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60">
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
          )}
        </div>

        <div className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Retainer liability</h2>
            <span className="text-sm text-gray-600">Total outstanding: ₹{report.total_outstanding.toLocaleString('en-IN')}</span>
          </div>
          {report.items.length === 0 ? (
            <p className="text-sm text-gray-500">No active retainers.</p>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500"><th className="sticky top-0 z-10 bg-white py-1">Employee</th><th className="sticky top-0 z-10 bg-white">₹/mo</th><th className="sticky top-0 z-10 bg-white">Period</th><th className="sticky top-0 z-10 bg-white">Outstanding</th></tr></thead>
              <tbody>
                {report.items.map((it) => (
                  <tr key={it.emptID} className="border-t border-[#f0ece2]">
                    {/* Name first, employee code as the fallback — never the raw
                        emptID (a UUID), which is what this column used to print. */}
                    <td className="py-1">
                      <span className="font-medium text-gray-800">
                        {it.name || it.employeeID || 'Unknown employee'}
                      </span>
                      {it.name && it.employeeID && (
                        <span className="ml-2 text-xs text-gray-400">{it.employeeID}</span>
                      )}
                    </td>
                    <td>{it.amount}</td><td>{it.period_months}</td>
                    <td>₹{Number(it.outstanding).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
      <Popup isOpen={popup.isOpen} type={popup.type} title={popup.title} message={popup.message} onClose={closePopup} />
    </div>
  );
}
